import InfiniteScroll from 'common/InfiniteScroll';
import {
  Meta,
  fetchHomePageWidgets,
  Widget,
} from 'common/apiService';
import * as Sentry from '@sentry/nextjs';
import Layout from 'common/Layout/Layout';
import WidgetManager from 'common/Components/Widgets/WidgetManager';
import usePagination from 'common/usePagination';
import WidgetLoading from 'common/WidgetsLoading';
import { useProfile, useUser } from '../common/CacheProvider';
import xIPCountry from 'common/apiService/xIPCountry';
import Seo from 'common/Seo';
import { VisuallyHidden } from 'common/styles';
import { requestAsViewer } from 'common/utils/authCookies';
import { RenderedAsViewerProvider } from 'common/utils/useViewerAccess';

type HomePageProps = {
  initialData: { data: Widget[], meta: Meta, },
  initialUrl: string,
  /** The widgets were requested with the viewer's token (from their cookie), so they're the viewer's own. */
  asViewer: boolean,
};

const HOME_PAGE_TITLE = 'فیلمنت: تماشای آنلاین فیلم و سریال';
const HOME_PAGE_DESCRIPTION = 'تماشای آنلاین فیلم و سریال در فیلمنت؛ آرشیو متنوع فیلم‌ها، سریال‌ها، سینما آنلاین و پخش زنده با امکان تماشا روی موبایل، لپ‌تاپ و تلویزیون.';

export default function HomePage({ initialData, initialUrl, asViewer }: HomePageProps) {
  const { authorized, user } = useUser();
  const { profile } = useProfile();
  // The further widgets are requested with the viewer's token too, like the first ones. Otherwise every
  // widget asks for the viewer's flags and continue watching itself (see useViewersOwnFlags).
  const { data: widgets, getInfiniteScrollProps, hasMore } = usePagination({
    initialData, initialUrl, viewer: asViewer && authorized ? `${user?.id}:${profile?.profileId}` : undefined,
  });
  return (
    <>
      <Seo title={HOME_PAGE_TITLE} description={HOME_PAGE_DESCRIPTION} path="/" />
      <VisuallyHidden as="h1">{HOME_PAGE_TITLE}</VisuallyHidden>

      <Layout disableFooter={hasMore}>
        {widgets.length > 0 && (
          <InfiniteScroll {...getInfiniteScrollProps()} loader={<WidgetLoading />}>
            <RenderedAsViewerProvider value={asViewer}>
              <WidgetManager widgets={widgets} serverTime={initialData.meta.server_date_time} />
            </RenderedAsViewerProvider>
          </InfiniteScroll>
        )}
      </Layout>
    </>
  );
}

export async function getServerSideProps({ req, res }) {
  xIPCountry({ req, res });

  const count = 5;
  const offset = 0;
  return await requestAsViewer(req, (config) => fetchHomePageWidgets({ count, offset }, config))
    .then(({ response: { data, meta }, asViewer }) => {
      return {
        props: {
          initialData: { data, meta },
          initialUrl: `/widgets/home-page?count=${count}`,
          asViewer,
        },
      };
    }).catch((err) => {
      Sentry.withScope((scope) => {
        scope.setTag('Status', err.response.status);
        scope.setTag('StatusText', err.response.statusText);
        scope.setTag('Url', err.response.config.url);
        Sentry.captureException(err.response);
      });
      throw new Error(err);
    });
}
